import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { SitterSearchCard } from 'us-react-components/dist/SitterSearchCard/SitterSearchCard';
import { useSitterSearchCardHandlers } from 'us-react-components/dist/hooks/useSitterSearchCardHandlers';
import {
  AffiliationService,
  CollegeService,
  CompanyService,
  GroupEntity,
  LocalGroupService,
  PagedResponseMeta,
  SchoolService,
  Sitter,
  SitterService,
} from 'us-web-services';


import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';
import { FilterSidebar } from '../Sitters/components/FilterSidebar';

export type GroupKind = 'school' | 'company' | 'college' | 'affiliation' | 'localGroup' | 'friend';

interface Props {
  kind: GroupKind;
}

type GroupWithLocation = GroupEntity & {
  displayName?: string;
  city?: { name?: string; state?: { code?: string } } | string;
  location?: { name?: string; state?: { code?: string } };
};

// Hero background images per group kind. Mirrors apps/angular/src/js/ng/parent/sitters/
// sitter-group-heading/sitter-group-heading.scss (`.sitters-{kind}` selectors).
const HERO_IMAGES: Partial<Record<GroupKind, { desktop: string; mobile?: string }>> = {
  school: {
    desktop: 'https://assets.urbansitter.com/static/img/backgrounds/school-hero-desktop.webp',
    mobile: 'https://assets.urbansitter.com/static/img/backgrounds/school-hero-mobile.webp',
  },
  affiliation: {
    desktop: 'https://assets.urbansitter.com/static/img/backgrounds/affiliation-hero-desktop.webp',
    mobile: 'https://assets.urbansitter.com/static/img/backgrounds/affiliation-hero-mobile.webp',
  },
  localGroup: {
    desktop: 'https://assets.urbansitter.com/static/img/backgrounds/local-hero-desktop.webp',
    mobile: 'https://assets.urbansitter.com/static/img/backgrounds/local-hero-mobile.webp',
  },
  company: {
    desktop: 'https://assets.urbansitter.com/static/img/backgrounds/company-hero-desktop.webp',
  },
};

const getLocationLabel = (g: GroupWithLocation | null): string => {
  if (!g) return '';
  const location = typeof g.city === 'object' ? g.city : g.location;
  if (location?.name && location.state?.code) {
    return `-- ${location.name}, ${location.state.code}`;
  }
  return '';
};

const MAX_KIDS_FOR_SEARCH = 4;

type GroupService = { getByFilter: (cfg: { params: Record<string, unknown> }) => Promise<{
  data?: { data?: GroupEntity[] };
}> };

const GROUP_SERVICES: Partial<Record<GroupKind, GroupService>> = {
  school: SchoolService as GroupService,
  company: CompanyService as GroupService,
  college: CollegeService as GroupService,
  affiliation: AffiliationService as GroupService,
  localGroup: LocalGroupService as GroupService,
};

const fetchGroupByAlias = async (
  kind: GroupKind,
  alias: string,
  userId: number,
): Promise<GroupEntity | null> => {
  const service = GROUP_SERVICES[kind];
  if (!service) return null;
  try {
    const r = (await service.getByFilter({ params: { alias, userId } })).data;
    return r?.data?.[0] ?? null;
  } catch (err) {
    console.warn('[GroupSitters] group fetch failed', err);
    return null;
  }
};

const filterKeyForKind = (kind: GroupKind): string => {
  if (kind === 'school') return 'schoolId';
  if (kind === 'company') return 'companyId';
  if (kind === 'college') return 'collegeId';
  if (kind === 'affiliation') return 'affiliationId';
  if (kind === 'localGroup') return 'localGroupId';
  return 'friendId';
};

export const GroupSitters = observer(({ kind }: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { name: aliasParam } = useParams<{ name: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;
  const numberOfChildren = Math.min(stores.parent.parent?.kids?.length || 1, MAX_KIDS_FOR_SEARCH);

  const [group, setGroup] = useState<GroupEntity | null>(null);
  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [groupMissing, setGroupMissing] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const pageParam = Number(searchParams.get('page') || 1) || 1;

  useEffect(() => {
    document.title = 'UrbanSitter - Caregivers';
  }, []);

  useEffect(() => {
    // friends path has no :name route param; everything else requires one.
    if (!user?.id) return;
    if (kind !== 'friend' && !aliasParam) {
      // Angular group-sitters redirects to explore-lists when the alias is
      // missing rather than leaving the page stuck in a perpetual load.
      setGroupMissing(true);
      setIsLoading(false);
      navigate(`/parent/${user.id}/explore-lists?companyError=true`, { replace: true });
      return;
    }
    let cancelled = false;

    const run = async (): Promise<void> => {
      if (kind === 'friend') {
        try {
          const resp = (
            await SitterService.getByFilter({
              params: { userId: user.id, parentId: user.id, friends: true, numberOfChildren, page: pageParam },
            })
          ).data;
          if (cancelled) return;
          setSitters(resp?.data ?? []);
          setMeta(resp?.meta ?? null);
        } catch (err) {
          console.warn('[GroupSitters] sitter load failed', err);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      const g = user.id !== undefined && aliasParam
        ? await fetchGroupByAlias(kind, aliasParam, user.id)
        : null;
      if (cancelled) return;
      if (!g?.id) {
        setGroupMissing(true);
        setIsLoading(false);
        navigate(`/parent/${user.id}/explore-lists?companyError=true`, { replace: true });
        return;
      }
      setGroup(g);

      try {
        const filterKey = filterKeyForKind(kind);
        const resp = (
          await SitterService.getByFilter({
            params: {
              userId: user.id,
              parentId: user.id,
              numberOfChildren,
              [filterKey]: g.id,
              page: pageParam,
            },
          })
        ).data;
        if (cancelled) return;
        setSitters(resp?.data ?? []);
        setMeta(resp?.meta ?? null);
      } catch (err) {
        console.warn('[GroupSitters] sitter load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return (): void => {
      cancelled = true;
    };
  }, [kind, aliasParam, user?.id, pageParam, navigate]);

  const { handleCardClick, handleToggleFavorite, handleBook } = useSitterSearchCardHandlers({
    userId: user?.id,
    track: tracker.track.bind(tracker),
    navigateToSitter: (id) => navigate(generatePath(Routes.sitter.profile, { id: String(id) })),
    navigateToBook: (id) => navigate(`/parent/bookings/select?sitterId=${id}`),
    logPrefix: 'GroupSitters',
  });

  const goToPage = (page: number): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.set('page', String(page));
        return next;
      },
      { replace: true },
    );
  };

  if (groupMissing) return <div className='container py-8' />;

  const totalPages = meta?.pageCount ?? 1;
  const totalResults = meta?.size ?? sitters.length;
  const groupWithLocation = group as GroupWithLocation | null;
  const groupName = groupWithLocation?.displayName ?? group?.name ?? '';
  const isCollege = kind === 'college';
  const isFriends = kind === 'friend';
  const isLocalGroup = kind === 'localGroup';
  const hero = HERO_IMAGES[kind];
  const locationLabel = getLocationLabel(groupWithLocation);

  const heroHeading = isLocalGroup
    ? t('pages.groups.heroHeadingLocal', { name: groupName })
    : t('pages.groups.heroHeading', { name: groupName });

  const heroSubtitle = isLocalGroup
    ? t('pages.groups.heroSubtitleLocal', { name: groupName })
    : t('pages.groups.heroSubtitle');

  const collegeHeading = t('pages.groups.collegeHeading', { name: groupName });
  const collegeSubtitle = t('pages.groups.collegeSubtitle', { name: groupName });
  const friendsHeading = t('pages.groups.friendsHeading');

  return (
    <div>
      {!isCollege && !isFriends && group && hero && (
        <div
          className='relative bg-cover bg-center bg-no-repeat min-h-[260px] md:min-h-[340px] flex flex-col justify-end'
          style={{ backgroundImage: `url(${hero.desktop})` }}
        >
          <div className='bg-black/55 text-white'>
            <div className='container py-4'>
              <h1 className='sr-only'>{heroHeading}</h1>
              <h2 className='text-xl md:text-2xl font-semibold mb-1'>
                {groupName}
                {locationLabel && (
                  <span className='font-light ml-2 hidden md:inline'>{locationLabel}</span>
                )}
              </h2>
              <h3 className='text-base md:text-lg font-light mb-0'>{heroSubtitle}</h3>
            </div>
          </div>
        </div>
      )}

      <div className='container py-6'>
      {(isCollege || isFriends) && (group || isFriends) && (
        <div className='mb-5'>
          {isCollege && (
            <i className='fa-solid fa-graduation-cap text-sapphire text-3xl mr-3 align-middle' />
          )}
          <h1 className='text-2xl font-semibold inline mb-1'>
            {isCollege ? collegeHeading : friendsHeading}
          </h1>
          {isCollege && <div className='mt-1 text-neutral-600'>{collegeSubtitle}</div>}
        </div>
      )}

      {isLoading && (
        <div className='text-neutral-600 text-center pt-4'>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          <span>{t('pages.groups.loading')}</span>
        </div>
      )}

      {!isLoading && sitters.length === 0 && (
        <div className='us-card rounded p-6'>
          <h3 className='mb-1 font-semibold'>{t('pages.groups.noMatchHeading')}</h3>
          <h4 className='font-light'>
            {t('pages.groups.noMatchBody')}{' '}
            <a
              id='filtered-full-search'
              href={'/app/sitters?verticalTypeId=1'}
              className='underline'
            >
              {t('pages.groups.goToFullSearch')}
            </a>{' '}
            {t('pages.groups.noMatchAfter')}
          </h4>
        </div>
      )}

      {!isLoading && sitters.length > 0 && (
        <>
          {!isFriends && group && (
            <div className='text-xs p-3 mb-4 text-neutral-600'>
              {!isCollege && (
                <>
                  {t('pages.groups.disclaimerPrefix', { name: groupName })}{' '}
                </>
              )}
              {t('pages.groups.disclaimerMiddle', { name: groupName })}{' '}
              {isCollege
                ? t('pages.groups.disclaimerCaregiver', { name: groupName })
                : t('pages.groups.disclaimerFamily', { name: groupName })}
            </div>
          )}

          <div className='text-sm text-neutral-600 mb-3'>
            {totalResults} {totalResults === 1 ? 'caregiver' : 'caregivers'}
          </div>

          <div className='mb-3 md:hidden'>
            <Button
              buttonType={ButtonType.secondary}
              onClick={(): void => setShowMobileFilters((v) => !v)}
            >
              <i className='fa-solid fa-sliders mr-1' />
              {t('pages.groups.filters')}
            </Button>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
            <div className={`md:col-span-3 ${showMobileFilters ? 'block' : 'hidden'} md:block`}>
              <FilterSidebar onFilterChange={(): void => setShowMobileFilters(false)} />
            </div>
            <div className='md:col-span-9'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {sitters.map((sitter) => (
                  <SitterSearchCard
                    key={sitter.id}
                    sitter={sitter}
                    numberOfChildren={numberOfChildren}
                    userPictureUrl={user?.pictureUrl}
                    onCardClick={handleCardClick}
                    onToggleFavorite={handleToggleFavorite}
                    onBook={handleBook}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className='flex items-center justify-center gap-3 mt-6'>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={pageParam <= 1}
                    onClick={(): void => goToPage(pageParam - 1)}
                  >
                    {t('pages.groups.previous')}
                  </Button>
                  <span className='text-sm'>
                    {t('pages.groups.pageOf', { current: pageParam, total: totalPages })}
                  </span>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={pageParam >= totalPages || meta?.lastPage === true}
                    onClick={(): void => goToPage(pageParam + 1)}
                  >
                    {t('pages.groups.next')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
});
